import type { CallbackDeliveryFilters } from "~/lib/queries";

/** Every filter on the callback deliveries list, as URL search params. */
export interface CallbackSearch {
  storefront_id?: string;
  order_id?: string;
  status?: string;
  event_type?: string;
  start_date?: string;
  end_date?: string;
  limit?: number;
  offset?: number;
}

const FILTER_KEYS = [
  "storefront_id",
  "order_id",
  "status",
  "event_type",
  "start_date",
  "end_date",
] as const satisfies readonly (keyof CallbackSearch)[];

/**
 * The status the screen opens on.
 *
 * `exhausted` is a delivery that used all eight attempts without a 2xx -- the
 * merchant was never told, and nothing will try again. It is the reason anyone
 * opens this screen, and it is rare enough that a default landing on it shows a
 * short list rather than a wall of successful deliveries.
 *
 * Written into the URL rather than applied invisibly, so the link says what it
 * shows. The All chip clears it.
 */
export const DEFAULT_STATUS = "exhausted";

/** ALL is what the "All" chip puts in the URL: chosen, and chosen to be empty. */
export const ALL = "all";

export function validateCallbackSearch(raw: Record<string, unknown>): CallbackSearch {
  return {
    storefront_id: str(raw.storefront_id),
    order_id: str(raw.order_id),
    status: status(raw.status),
    event_type: str(raw.event_type),
    start_date: str(raw.start_date),
    end_date: str(raw.end_date),
    limit: num(raw.limit),
    offset: num(raw.offset),
  };
}

/** Absent means "not chosen", which is where the default applies; ALL means
 *  "chosen, and no filter". The two must stay distinguishable. */
function status(value: unknown): string | undefined {
  const parsed = str(value);
  return parsed === undefined ? DEFAULT_STATUS : parsed;
}

/** True when the result set is narrowed beyond the screen's own default. */
export function hasActiveFilters(search: CallbackSearch): boolean {
  return FILTER_KEYS.some((key) => {
    if (key === "status") return search.status !== DEFAULT_STATUS;
    return search[key] !== undefined;
  });
}

export function toFilters(search: CallbackSearch): CallbackDeliveryFilters {
  return {
    storefront_id: search.storefront_id,
    order_id: search.order_id,
    status: search.status === ALL ? undefined : search.status,
    event_type: search.event_type,
    start_date: search.start_date,
    end_date: search.end_date,
    limit: search.limit ?? 25,
    offset: search.offset ?? 0,
  };
}

function str(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed === "" ? undefined : trimmed;
}

function num(value: unknown): number | undefined {
  const parsed = typeof value === "number" ? value : Number(str(value));
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined;
}
